/**
 * Export Service for Billing Data
 * Handles CSV and PDF export functionality
 */
/* eslint-disable max-classes-per-file */

import appConfig from '../config/appConfig';

export class ExportService {
  /**
   * Get currency symbol from config
   * @returns {string} Currency symbol
   */
  static getCurrencySymbol() {
    return appConfig.getCurrencySymbol();
  }

  /**
   * Exports billing records to CSV format with dynamic cost categories
   * @param {Array} records - Array of billing records
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportToCSV(records, filename = 'billing_records.csv') {
    if (!records || records.length === 0) {
      throw new Error('No records to export');
    }

    const currencySymbol = this.getCurrencySymbol();

    // Get all unique cost categories from the records
    const allCategories = this.getAllCostCategories(records);

    // Define dynamic CSV headers
    const baseHeaders = [
      'Record ID',
      'Billing Start Date',
      'Billing End Date',
      'Duration (Days)',
      'Initial Reading (kWh)',
      'Final Reading (kWh)',
      'Total Consumption (kWh)',
      'Actual kWh from Bill',
      'kVA Demand',
    ];

    // Add dynamic charge headers
    const chargeHeaders = allCategories.map(
      (category) => `${this.formatCostCategory(category)} (${currencySymbol})`,
    );

    const endHeaders = [
      `Total Calculated (${currencySymbol})`,
      `Actual Amount (${currencySymbol})`,
      `Variance (${currencySymbol})`,
      'Variance (%)',
      `Per Unit Charge (${currencySymbol}/kWh)`,
      'Created By',
      'Created Date',
    ];

    const headers = [...baseHeaders, ...chargeHeaders, ...endHeaders];

    // Convert records to CSV rows
    const csvRows = records.map((record) => {
      const variance = record.variance || 0;
      const calculatedTotal = this.getCalculatedTotal(record);
      const variancePercentage = calculatedTotal > 0
        ? ((Math.abs(variance) / calculatedTotal) * 100).toFixed(2)
        : '0.00';

      const duration = this.calculateBillingDuration(record.billingPeriod);
      const perUnitCharge = record.consumptionData.totalConsumption > 0
        ? (record.actualBillAmount / record.consumptionData.totalConsumption).toFixed(2)
        : '0.00';

      // Base data
      const baseData = [
        record._id || record.id,
        this.formatDateForCSV(record.billingPeriod.startDate),
        this.formatDateForCSV(record.billingPeriod.endDate),
        duration,
        record.consumptionData.initialReading,
        record.consumptionData.finalReading,
        record.consumptionData.totalConsumption,
        record.actualKwhFromBill || 0,
        record.kvaDemand,
      ];

      // Dynamic charge data
      const chargeData = allCategories.map((category) => record.calculatedCharges?.[category] || 0);

      // End data
      const endData = [
        calculatedTotal,
        record.actualBillAmount,
        variance,
        variancePercentage,
        perUnitCharge,
        record.createdBy || 'operator',
        this.formatDateTimeForCSV(record.createdAt),
      ];

      return [...baseData, ...chargeData, ...endData];
    });

    // Combine headers and rows
    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');

    // Create and download file
    this.downloadFile(csvContent, filename, 'text/csv');
  }

  /**
   * Exports a single billing record to detailed CSV
   * @param {Object} record - Billing record
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportRecordToCSV(record, filename = null) {
    if (!record) {
      throw new Error('No record to export');
    }

    const recordId = record._id || record.id || 'unknown';
    const defaultFilename = `billing_record_${recordId}.csv`;
    const csvContent = this.generateDetailedRecordCSV(record);
    this.downloadFile(csvContent, filename || defaultFilename, 'text/csv');
  }

  /**
   * Exports billing records summary to CSV
   * @param {Array} records - Array of billing records
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportSummaryToCSV(records, filename = 'billing_summary.csv') {
    if (!records || records.length === 0) {
      throw new Error('No records to export');
    }

    const currencySymbol = this.getCurrencySymbol();

    // Calculate summary statistics
    const totalRecords = records.length;
    const totalCalculated = records.reduce((sum, r) => sum + this.getCalculatedTotal(r), 0);
    const totalActual = records.reduce((sum, r) => sum + r.actualBillAmount, 0);
    const totalVariance = totalActual - totalCalculated;
    const totalConsumption = records.reduce(
      (sum, r) => sum + r.consumptionData.totalConsumption,
      0,
    );
    const totalActualKwh = records.reduce((sum, r) => sum + (r.actualKwhFromBill || 0), 0);
    const averageConsumption = totalConsumption / totalRecords;
    const averageActualKwh = totalActualKwh / totalRecords;

    // Variance analysis
    const perfectMatch = records.filter((r) => this.getVariancePercentage(r) < 1).length;
    const acceptable = records.filter((r) => {
      const pct = this.getVariancePercentage(r);
      return pct >= 1 && pct <= 5;
    }).length;
    const significant = records.filter((r) => this.getVariancePercentage(r) > 5).length;

    const summaryData = [
      ['Billing Records Summary', ''],
      ['Generated On', this.formatDateTimeForCSV(new Date())],
      ['', ''],
      ['OVERVIEW', ''],
      ['Total Records', totalRecords],
      [
        'Date Range',
        `${this.formatDateForCSV(records[0].billingPeriod.startDate)} to ${
          this.formatDateForCSV(records[records.length - 1].billingPeriod.endDate)
        }`,
      ],
      ['', ''],
      ['FINANCIAL SUMMARY', ''],
      [`Total Calculated Amount (${currencySymbol})`, totalCalculated.toLocaleString()],
      [`Total Actual Amount (${currencySymbol})`, totalActual.toLocaleString()],
      [`Total Variance (${currencySymbol})`, totalVariance.toLocaleString()],
      ['Average Variance (%)', ((Math.abs(totalVariance) / totalCalculated) * 100).toFixed(2)],
      ['', ''],
      ['CONSUMPTION SUMMARY', ''],
      ['Total Calculated Consumption (kWh)', totalConsumption.toLocaleString()],
      ['Total Actual kWh from Bills', totalActualKwh.toLocaleString()],
      ['Average Calculated Consumption per Record (kWh)', averageConsumption.toFixed(2)],
      ['Average Actual kWh per Record', averageActualKwh.toFixed(2)],
      ['', ''],
      ['VARIANCE ANALYSIS', ''],
      ['Perfect Match (<1%)', perfectMatch],
      ['Acceptable Variance (1-5%)', acceptable],
      ['Significant Variance (>5%)', significant],
    ];

    const csvContent = summaryData
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');

    this.downloadFile(csvContent, filename, 'text/csv');
  }

  /**
   * Exports bill comparison data to CSV
   * @param {Object} comparisonData - Comparison values to export
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportComparisonToCSV(comparisonData, filename = 'bill_comparison.csv') {
    if (!comparisonData) {
      throw new Error('No comparison data to export');
    }

    const currencySymbol = this.getCurrencySymbol();
    const exportDate = comparisonData.exportDate
      ? this.formatDateTimeForCSV(comparisonData.exportDate)
      : this.formatDateTimeForCSV(new Date());
    const variancePercentage = Number(comparisonData.variancePercentage || 0).toFixed(2);

    const comparisonRows = [
      ['Bill Comparison', ''],
      ['Generated On', exportDate],
      ['', ''],
      [`Calculated Amount (${currencySymbol})`, comparisonData.calculatedAmount],
      [`Actual Amount (${currencySymbol})`, comparisonData.actualAmount],
      [`Variance (${currencySymbol})`, comparisonData.variance],
      ['Variance (%)', variancePercentage],
      ['Variance Direction', comparisonData.varianceDirection],
      ['Variance Status', comparisonData.status],
    ];

    const csvContent = comparisonRows
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');

    this.downloadFile(csvContent, filename, 'text/csv');
  }

  /**
   * Gets all unique cost categories from billing records
   * @param {Array} records - Array of billing records
   * @returns {Array} Array of unique cost categories
   */
  static getAllCostCategories(records) {
    const categories = new Set();

    records.forEach((record) => {
      if (record.calculatedCharges) {
        Object.keys(record.calculatedCharges).forEach((category) => {
          if (
            category !== 'totalCalculated'
            && typeof record.calculatedCharges[category] === 'number'
          ) {
            categories.add(category);
          }
        });
      }
    });

    return Array.from(categories).sort();
  }

  /**
   * Generates a detailed CSV for a single record with dynamic charges
   * @param {Object} record - Billing record
   * @returns {string} CSV content
   */
  static generateDetailedRecordCSV(record) {
    const variance = record.variance || 0;
    const variancePercentage = this.getVariancePercentage(record);
    const duration = this.calculateBillingDuration(record.billingPeriod);
    const calculatedTotal = this.getCalculatedTotal(record);
    const perUnitCharge = record.consumptionData.totalConsumption > 0
      ? (record.actualBillAmount / record.consumptionData.totalConsumption).toFixed(2)
      : '0.00';

    const currencySymbol = this.getCurrencySymbol();

    const detailData = [
      ['Billing Record Detail', ''],
      ['Record ID', record._id || record.id],
      ['Created By', record.createdBy || 'operator'],
      ['Created Date', this.formatDateTimeForCSV(record.createdAt)],
      ['', ''],
      ['BILLING PERIOD', ''],
      ['Start Date', this.formatDateForCSV(record.billingPeriod.startDate)],
      ['End Date', this.formatDateForCSV(record.billingPeriod.endDate)],
      ['Duration (Days)', duration],
      ['', ''],
      ['CONSUMPTION DATA', ''],
      ['Initial Reading (kWh)', record.consumptionData.initialReading],
      ['Final Reading (kWh)', record.consumptionData.finalReading],
      ['Total Calculated Consumption (kWh)', record.consumptionData.totalConsumption],
      ['Actual kWh from Bill', record.actualKwhFromBill || 0],
      [
        'Average Daily Consumption (kWh)',
        (record.consumptionData.totalConsumption / duration).toFixed(2),
      ],
      ['kVA Demand', record.kvaDemand],
      ['', ''],
      [`CALCULATED CHARGES (${currencySymbol})`, ''],
    ];

    // Add dynamic charges
    if (record.calculatedCharges) {
      // Separate base and regulatory charges
      const baseCharges = [];
      const regulatoryCharges = [];

      Object.entries(record.calculatedCharges).forEach(([category, value]) => {
        if (category !== 'totalCalculated' && typeof value === 'number') {
          const chargeEntry = [this.formatCostCategory(category), value];

          if (this.isRegulatoryCharge(category)) {
            regulatoryCharges.push(chargeEntry);
          } else {
            baseCharges.push(chargeEntry);
          }
        }
      });

      // Add base charges
      baseCharges.forEach(([name, value]) => {
        detailData.push([name, value]);
      });

      // Add subtotal
      const subtotal = baseCharges.reduce((sum, [, value]) => sum + value, 0);
      detailData.push(['Subtotal (Base Charges)', subtotal]);

      // Add regulatory charges
      regulatoryCharges.forEach(([name, value]) => {
        detailData.push([name, value]);
      });
    }

    // Add totals and comparison
    detailData.push(['Total Calculated', calculatedTotal]);
    detailData.push(['', '']);
    detailData.push(['ACTUAL VS CALCULATED', '']);
    detailData.push([`Actual Bill Amount (${currencySymbol})`, record.actualBillAmount]);
    detailData.push([`Per Unit Charge (${currencySymbol}/kWh)`, perUnitCharge]);
    detailData.push([`Variance (${currencySymbol})`, variance]);
    detailData.push(['Variance (%)', variancePercentage.toFixed(2)]);
    detailData.push(['Variance Status', this.getVarianceStatus(variancePercentage)]);

    // Add rates used if available
    if (record.ratesUsed && record.ratesUsed.length > 0) {
      detailData.push(['', '']);
      detailData.push(['RATES USED', '']);
      detailData.push(['Category', 'Rate', 'Type', 'Effective From', 'Effective To']);

      record.ratesUsed.forEach((rate) => {
        detailData.push([
          this.formatCostCategory(rate.costCategory),
          rate.rateType === 'PERCENTAGE' ? `${rate.rate}%` : rate.rate.toLocaleString(),
          rate.rateType,
          this.formatDateForCSV(rate.effectiveFrom),
          this.formatDateForCSV(rate.effectiveTo),
        ]);
      });
    }

    return detailData
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');
  }

  /**
   * Creates and downloads a file
   * @param {string} content - File content
   * @param {string} filename - File name
   * @param {string} mimeType - MIME type
   * @returns {void}
   */
  static downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  /**
   * Helper methods for formatting and calculations
   */
  static formatDateForCSV(date) {
    return new Date(date).toLocaleDateString('en-US');
  }

  static formatDateTimeForCSV(date) {
    return new Date(date).toLocaleString('en-US');
  }

  static calculateBillingDuration(period) {
    const start = new Date(period.startDate);
    const end = new Date(period.endDate);
    const diffTime = Math.abs(end - start);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  static getVariancePercentage(record) {
    const calculatedTotal = this.getCalculatedTotal(record);
    if (!calculatedTotal) return 0;
    const variance = Math.abs(record.variance || 0);
    return ((variance / calculatedTotal) * 100);
  }

  /**
   * Gets the calculated total from either old or new format
   * @param {Object} record - Billing record
   * @returns {number} Calculated total
   */
  static getCalculatedTotal(record) {
    if (!record.calculatedCharges) return 0;

    // New dynamic format
    if (record.calculatedCharges.totalCalculated !== undefined) {
      return record.calculatedCharges.totalCalculated;
    }

    return Object.entries(record.calculatedCharges).reduce((total, [key, value]) => {
      if (key !== 'totalCalculated' && typeof value === 'number') {
        return total + value;
      }
      return total;
    }, 0);
  }

  /**
   * Helper method to determine if a charge category is regulatory
   * @param {string} category - Cost category
   * @returns {boolean} True if regulatory charge
   */
  static isRegulatoryCharge(category) {
    if (!category || typeof category !== 'string') return false;

    const regulatoryTerms = [
      'VAT', 'TAX', 'REA', 'EWURA', 'LEVY', 'FEE', 'NHIL', 'GETFUND',
      'KEBS', 'ERC', 'NATIONAL_ELECTRIFICATION', 'STREET_LIGHT',
      'REGULATORY', 'GOVERNMENT',
    ];
    return regulatoryTerms.some((term) => category.toUpperCase().includes(term));
  }

  /**
   * Formats cost category name for display
   * @param {string} category - Category code
   * @returns {string} Formatted category name
   */
  static formatCostCategory(category) {
    if (!category) return 'Unknown Category';

    // Convert snake_case or UPPER_CASE to Title Case
    return category
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  static getVarianceStatus(percentage) {
    if (percentage < 1) return 'Perfect Match';
    if (percentage <= 5) return 'Acceptable';
    return 'Significant';
  }

  /**
   * DG BILLING EXPORT METHODS
   */

  /**
   * Exports DG billing records to CSV format
   * @param {Array} records - Array of DG billing records
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportDGBillingToCSV(records, filename = 'dg_billing_records.csv') {
    if (!records || records.length === 0) {
      // Handle empty data exports gracefully
      const emptyContent = 'No DG billing records available for export\n';
      this.downloadFile(emptyContent, filename, 'text/csv');
      return;
    }

    // Define CSV headers for DG billing
    const headers = [
      'Record ID',
      'Billing Start Date',
      'Billing End Date',
      'Duration (Days)',
      'Initial DG Reading (kWh)',
      'Final DG Reading (kWh)',
      'Total DG Consumption (kWh)',
      'Diesel Consumption (Litres)',
      'Running Hours',
      'kWh per Litre Efficiency',
      'Litres per Hour Efficiency',
      'Average Daily Consumption (kWh)',
      'Average Fuel Rate (L/day)',
      'Created By',
      'Created Date',
    ];

    // Convert DG records to CSV rows
    const csvRows = records.map((record) => {
      const duration = this.calculateBillingDuration(record.billingPeriod);
      const avgDailyConsumption = (record.dgConsumptionData.totalConsumption / duration).toFixed(2);
      const avgFuelRate = (record.dieselConsumption / duration).toFixed(2);

      return [
        record._id || record.id,
        this.formatDateForCSV(record.billingPeriod.startDate),
        this.formatDateForCSV(record.billingPeriod.endDate),
        duration,
        record.dgConsumptionData.initialReading,
        record.dgConsumptionData.finalReading,
        record.dgConsumptionData.totalConsumption,
        record.dieselConsumption,
        record.runningHours,
        record.efficiencyMetrics.kwhPerLitre.toFixed(2),
        record.efficiencyMetrics.litresPerHour.toFixed(2),
        avgDailyConsumption,
        avgFuelRate,
        record.createdBy || 'operator',
        this.formatDateTimeForCSV(record.createdAt),
      ];
    });

    // Combine headers and rows
    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');

    // Create and download file
    this.downloadFile(csvContent, filename, 'text/csv');
  }

  /**
   * Exports a single DG billing record to detailed CSV
   * @param {Object} record - DG billing record
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportDGRecordToCSV(record, filename = null) {
    if (!record) {
      throw new Error('No DG record to export');
    }

    const recordId = record._id || record.id || 'unknown';
    const defaultFilename = `dg_billing_record_${recordId}.csv`;
    const csvContent = this.generateDetailedDGRecordCSV(record);
    this.downloadFile(csvContent, filename || defaultFilename, 'text/csv');
  }

  /**
   * Exports DG billing records summary to CSV with statistics
   * @param {Array} records - Array of DG billing records
   * @param {string} filename - Optional filename
   * @returns {void}
   */
  static exportDGSummaryToCSV(records, filename = 'dg_billing_summary.csv') {
    if (!records || records.length === 0) {
      // Handle empty data exports gracefully
      const emptyContent = 'No DG billing records available for summary export\n';
      this.downloadFile(emptyContent, filename, 'text/csv');
      return;
    }

    // Calculate summary statistics for DG billing
    const totalRecords = records.length;
    const totalDGConsumption = records.reduce(
      (sum, r) => sum + r.dgConsumptionData.totalConsumption,
      0,
    );
    const totalDieselConsumption = records.reduce((sum, r) => sum + r.dieselConsumption, 0);
    const totalRunningHours = records.reduce((sum, r) => sum + r.runningHours, 0);

    // Calculate averages
    const avgDGConsumption = totalDGConsumption / totalRecords;
    const avgDieselConsumption = totalDieselConsumption / totalRecords;
    const avgRunningHours = totalRunningHours / totalRecords;

    // Calculate overall efficiency metrics
    const overallKwhPerLitre = totalDieselConsumption > 0
      ? totalDGConsumption / totalDieselConsumption
      : 0;
    const overallLitresPerHour = totalRunningHours > 0
      ? totalDieselConsumption / totalRunningHours
      : 0;

    // Efficiency analysis
    const highEfficiency = records.filter((r) => r.efficiencyMetrics.kwhPerLitre >= 3.0).length;
    const mediumEfficiency = records.filter((r) => {
      const eff = r.efficiencyMetrics.kwhPerLitre;
      return eff >= 2.0 && eff < 3.0;
    }).length;
    const lowEfficiency = records.filter((r) => r.efficiencyMetrics.kwhPerLitre < 2.0).length;

    // Calculate date range
    const sortedRecords = [...records].sort(
      (a, b) => new Date(a.billingPeriod.startDate) - new Date(b.billingPeriod.startDate),
    );
    const dateRange = `${this.formatDateForCSV(sortedRecords[0].billingPeriod.startDate)} to ${
      this.formatDateForCSV(sortedRecords[sortedRecords.length - 1].billingPeriod.endDate)
    }`;

    const summaryData = [
      ['DG Billing Records Summary', ''],
      ['Generated On', this.formatDateTimeForCSV(new Date())],
      ['', ''],
      ['OVERVIEW', ''],
      ['Total Records', totalRecords],
      ['Date Range', dateRange],
      ['', ''],
      ['CONSUMPTION SUMMARY', ''],
      ['Total DG Consumption (kWh)', totalDGConsumption.toLocaleString()],
      ['Average DG Consumption per Record (kWh)', avgDGConsumption.toFixed(2)],
      ['Total Diesel Consumption (Litres)', totalDieselConsumption.toLocaleString()],
      ['Average Diesel Consumption per Record (Litres)', avgDieselConsumption.toFixed(2)],
      ['Total Running Hours', totalRunningHours.toLocaleString()],
      ['Average Running Hours per Record', avgRunningHours.toFixed(2)],
      ['', ''],
      ['EFFICIENCY METRICS', ''],
      ['Overall kWh per Litre Efficiency', overallKwhPerLitre.toFixed(2)],
      ['Overall Litres per Hour Efficiency', overallLitresPerHour.toFixed(2)],
      ['', ''],
      ['EFFICIENCY ANALYSIS', ''],
      ['High Efficiency Records (≥3.0 kWh/L)', highEfficiency],
      ['Medium Efficiency Records (2.0-2.9 kWh/L)', mediumEfficiency],
      ['Low Efficiency Records (<2.0 kWh/L)', lowEfficiency],
      ['', ''],
      ['PERFORMANCE INDICATORS', ''],
      [
        'Average Daily DG Consumption (kWh/day)',
        (totalDGConsumption / this.calculateTotalDays(records)).toFixed(2),
      ],
      [
        'Average Daily Fuel Consumption (L/day)',
        (totalDieselConsumption / this.calculateTotalDays(records)).toFixed(2),
      ],
      [
        'Average Daily Running Hours (hrs/day)',
        (totalRunningHours / this.calculateTotalDays(records)).toFixed(2),
      ],
    ];

    const csvContent = summaryData
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');

    this.downloadFile(csvContent, filename, 'text/csv');
  }

  /**
   * Generates a detailed CSV for a single DG record
   * @param {Object} record - DG billing record
   * @returns {string} CSV content
   */
  static generateDetailedDGRecordCSV(record) {
    const duration = this.calculateBillingDuration(record.billingPeriod);
    const avgDailyConsumption = (record.dgConsumptionData.totalConsumption / duration).toFixed(2);
    const avgDailyFuel = (record.dieselConsumption / duration).toFixed(2);
    const avgDailyHours = (record.runningHours / duration).toFixed(2);

    const detailData = [
      ['DG Billing Record Detail', ''],
      ['Record ID', record._id || record.id],
      ['Created By', record.createdBy || 'operator'],
      ['Created Date', this.formatDateTimeForCSV(record.createdAt)],
      ['Updated Date', record.updatedAt ? this.formatDateTimeForCSV(record.updatedAt) : 'N/A'],
      ['', ''],
      ['BILLING PERIOD', ''],
      ['Start Date', this.formatDateForCSV(record.billingPeriod.startDate)],
      ['End Date', this.formatDateForCSV(record.billingPeriod.endDate)],
      ['Duration (Days)', duration],
      ['', ''],
      ['DG CONSUMPTION DATA', ''],
      ['Initial DG Reading (kWh)', record.dgConsumptionData.initialReading],
      ['Final DG Reading (kWh)', record.dgConsumptionData.finalReading],
      ['Total DG Consumption (kWh)', record.dgConsumptionData.totalConsumption],
      ['Average Daily DG Consumption (kWh)', avgDailyConsumption],
      ['', ''],
      ['FUEL AND OPERATIONAL DATA', ''],
      ['Diesel Consumption (Litres)', record.dieselConsumption],
      ['Running Hours', record.runningHours],
      ['Average Daily Fuel Consumption (Litres)', avgDailyFuel],
      ['Average Daily Running Hours', avgDailyHours],
      ['', ''],
      ['EFFICIENCY METRICS', ''],
      ['kWh per Litre Efficiency', record.efficiencyMetrics.kwhPerLitre.toFixed(2)],
      ['Litres per Hour Efficiency', record.efficiencyMetrics.litresPerHour.toFixed(2)],
      ['Fuel Efficiency Status', this.getDGEfficiencyStatus(record.efficiencyMetrics.kwhPerLitre)],
      ['', ''],
      ['PERFORMANCE ANALYSIS', ''],
      [
        'Power Generation Rate (kWh/hr)',
        (record.dgConsumptionData.totalConsumption / record.runningHours).toFixed(2),
      ],
      ['Fuel Consumption Rate (L/hr)', (record.dieselConsumption / record.runningHours).toFixed(2)],
      ['Load Factor (%)', this.calculateDGLoadFactor(record).toFixed(2)],
    ];

    return detailData
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');
  }

  /**
   * Generates filename with timestamp for DG billing exports
   * @param {string} baseFilename - Base filename
   * @param {string} recordId - Optional record ID
   * @returns {string} Generated filename
   */
  static generateDGExportFilename(baseFilename, recordId = null) {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    const idSuffix = recordId ? `_${recordId}` : '';
    const extension = baseFilename.endsWith('.csv') ? '' : '.csv';
    return `${baseFilename}${idSuffix}_${timestamp}${extension}`;
  }

  /**
   * Helper methods for DG billing calculations
   */

  /**
   * Calculates total days across all DG billing records
   * @param {Array} records - Array of DG billing records
   * @returns {number} Total days
   */
  static calculateTotalDays(records) {
    return records.reduce(
      (total, record) => total + this.calculateBillingDuration(record.billingPeriod),
      0,
    );
  }

  /**
   * Gets DG efficiency status based on kWh per litre
   * @param {number} kwhPerLitre - Efficiency value
   * @returns {string} Efficiency status
   */
  static getDGEfficiencyStatus(kwhPerLitre) {
    if (kwhPerLitre >= 3.0) return 'High Efficiency';
    if (kwhPerLitre >= 2.0) return 'Medium Efficiency';
    return 'Low Efficiency';
  }

  /**
   * Calculates DG load factor (simplified estimation)
   * @param {Object} record - DG billing record
   * @returns {number} Load factor percentage
   */
  static calculateDGLoadFactor(record) {
    // Simplified load factor calculation
    // Assumes typical DG capacity and calculates based on actual vs potential output
    const avgPowerOutput = record.dgConsumptionData.totalConsumption / record.runningHours;
    const estimatedCapacity = avgPowerOutput * 1.5; // Rough estimation
    return estimatedCapacity > 0 ? (avgPowerOutput / estimatedCapacity) * 100 : 0;
  }
}

/**
 * Search Service for Billing Records
 * Provides advanced search and filtering capabilities
 */
export class SearchService {
  /**
   * Performs advanced search on billing records
   * @param {Array} records - Array of billing records
   * @param {Object} searchCriteria - Search criteria
   * @returns {Array} Filtered records
   */
  static searchRecords(records, searchCriteria) {
    if (!records || records.length === 0) {
      return [];
    }

    let filtered = [...records];

    // Text search across multiple fields
    if (searchCriteria.searchText) {
      const searchText = searchCriteria.searchText.toLowerCase();
      filtered = filtered.filter((record) => (
        record.id.toLowerCase().includes(searchText)
          || (record.createdBy && record.createdBy.toLowerCase().includes(searchText))
          || record.consumptionData.totalConsumption.toString().includes(searchText)
          || record.kvaDemand.toString().includes(searchText)
          || ExportService.getCalculatedTotal(record).toString().includes(searchText)
          || record.actualBillAmount.toString().includes(searchText)
      ));
    }

    // Date range filters
    if (searchCriteria.startDate) {
      const startDate = new Date(searchCriteria.startDate);
      filtered = filtered.filter((record) => {
        const recordDate = new Date(record.billingPeriod.startDate);
        return recordDate >= startDate;
      });
    }

    if (searchCriteria.endDate) {
      const endDate = new Date(searchCriteria.endDate);
      filtered = filtered.filter((record) => {
        const recordDate = new Date(record.billingPeriod.endDate);
        return recordDate <= endDate;
      });
    }

    // Amount range filters
    if (searchCriteria.minAmount !== null && searchCriteria.minAmount !== undefined) {
      const minAmount = parseFloat(searchCriteria.minAmount);
      filtered = filtered.filter((record) => ExportService.getCalculatedTotal(record) >= minAmount);
    }

    if (searchCriteria.maxAmount !== null && searchCriteria.maxAmount !== undefined) {
      const maxAmount = parseFloat(searchCriteria.maxAmount);
      filtered = filtered.filter((record) => ExportService.getCalculatedTotal(record) <= maxAmount);
    }

    // Consumption range filters
    if (searchCriteria.minConsumption !== null && searchCriteria.minConsumption !== undefined) {
      const minConsumption = parseFloat(searchCriteria.minConsumption);
      filtered = filtered.filter(
        (record) => record.consumptionData.totalConsumption >= minConsumption,
      );
    }

    if (searchCriteria.maxConsumption !== null && searchCriteria.maxConsumption !== undefined) {
      const maxConsumption = parseFloat(searchCriteria.maxConsumption);
      filtered = filtered.filter(
        (record) => record.consumptionData.totalConsumption <= maxConsumption,
      );
    }

    // Variance status filter
    if (searchCriteria.varianceStatus) {
      filtered = filtered.filter((record) => {
        const variancePercentage = ExportService.getVariancePercentage(record);

        switch (searchCriteria.varianceStatus) {
          case 'perfect':
            return variancePercentage < 1;
          case 'acceptable':
            return variancePercentage >= 1 && variancePercentage <= 5;
          case 'significant':
            return variancePercentage > 5;
          default:
            return true;
        }
      });
    }

    // Created by filter
    if (searchCriteria.createdBy) {
      const createdBy = searchCriteria.createdBy.toLowerCase();
      filtered = filtered.filter(
        (record) => record.createdBy && record.createdBy.toLowerCase().includes(createdBy),
      );
    }

    // Sort results
    if (searchCriteria.sortBy) {
      filtered = this.sortRecords(filtered, searchCriteria.sortBy, searchCriteria.sortDesc);
    }

    return filtered;
  }

  /**
   * Sorts records by specified field
   * @param {Array} records - Records to sort
   * @param {string} sortBy - Field to sort by
   * @param {boolean} sortDesc - Sort in descending order
   * @returns {Array} Sorted records
   */
  static sortRecords(records, sortBy, sortDesc = false) {
    const sorted = [...records].sort((a, b) => {
      let aVal; let
        bVal;

      switch (sortBy) {
        case 'billingPeriod':
          aVal = new Date(a.billingPeriod.startDate);
          bVal = new Date(b.billingPeriod.startDate);
          break;
        case 'totalConsumption':
          aVal = a.consumptionData.totalConsumption;
          bVal = b.consumptionData.totalConsumption;
          break;
        case 'kvaDemand':
          aVal = a.kvaDemand;
          bVal = b.kvaDemand;
          break;
        case 'calculatedAmount':
          aVal = ExportService.getCalculatedTotal(a);
          bVal = ExportService.getCalculatedTotal(b);
          break;
        case 'actualAmount':
          aVal = a.actualBillAmount;
          bVal = b.actualBillAmount;
          break;
        case 'variance':
          aVal = Math.abs(a.variance || 0);
          bVal = Math.abs(b.variance || 0);
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
          break;
        default:
          aVal = a[sortBy];
          bVal = b[sortBy];
      }

      if (aVal < bVal) return sortDesc ? 1 : -1;
      if (aVal > bVal) return sortDesc ? -1 : 1;
      return 0;
    });

    return sorted;
  }

  /**
   * Gets search suggestions based on existing records
   * @param {Array} records - Array of billing records
   * @param {string} field - Field to get suggestions for
   * @returns {Array} Array of suggestions
   */
  static getSearchSuggestions(records, field) {
    if (!records || records.length === 0) {
      return [];
    }

    const suggestions = new Set();

    records.forEach((record) => {
      switch (field) {
        case 'createdBy':
          if (record.createdBy) {
            suggestions.add(record.createdBy);
          }
          break;
        case 'amounts':
          suggestions.add(ExportService.getCalculatedTotal(record));
          suggestions.add(record.actualBillAmount);
          break;
        case 'consumption':
          suggestions.add(record.consumptionData.totalConsumption);
          break;
        default:
          break;
      }
    });

    return Array.from(suggestions).sort();
  }

  /**
   * DG BILLING SEARCH METHODS
   */

  /**
   * Performs advanced search on DG billing records
   * @param {Array} records - Array of DG billing records
   * @param {Object} searchCriteria - Search criteria
   * @returns {Array} Filtered DG records
   */
  static searchDGRecords(records, searchCriteria) {
    if (!records || records.length === 0) {
      return [];
    }

    let filtered = [...records];

    // Text search across multiple fields
    if (searchCriteria.searchText) {
      const searchText = searchCriteria.searchText.toLowerCase();
      filtered = filtered.filter((record) => (
        (record.id && record.id.toLowerCase().includes(searchText))
          || (record._id && record._id.toLowerCase().includes(searchText))
          || (record.createdBy && record.createdBy.toLowerCase().includes(searchText))
          || record.dgConsumptionData.totalConsumption.toString().includes(searchText)
          || record.dieselConsumption.toString().includes(searchText)
          || record.runningHours.toString().includes(searchText)
          || record.efficiencyMetrics.kwhPerLitre.toString().includes(searchText)
          || record.efficiencyMetrics.litresPerHour.toString().includes(searchText)
      ));
    }

    // Date range filters
    if (searchCriteria.startDate) {
      const startDate = new Date(searchCriteria.startDate);
      filtered = filtered.filter((record) => {
        const recordDate = new Date(record.billingPeriod.startDate);
        return recordDate >= startDate;
      });
    }

    if (searchCriteria.endDate) {
      const endDate = new Date(searchCriteria.endDate);
      filtered = filtered.filter((record) => {
        const recordDate = new Date(record.billingPeriod.endDate);
        return recordDate <= endDate;
      });
    }

    // DG consumption range filters
    if (searchCriteria.minDGConsumption !== null && searchCriteria.minDGConsumption !== undefined) {
      const minConsumption = parseFloat(searchCriteria.minDGConsumption);
      filtered = filtered.filter(
        (record) => record.dgConsumptionData.totalConsumption >= minConsumption,
      );
    }

    if (searchCriteria.maxDGConsumption !== null && searchCriteria.maxDGConsumption !== undefined) {
      const maxConsumption = parseFloat(searchCriteria.maxDGConsumption);
      filtered = filtered.filter(
        (record) => record.dgConsumptionData.totalConsumption <= maxConsumption,
      );
    }

    // Diesel consumption range filters
    if (
      searchCriteria.minDieselConsumption !== null
      && searchCriteria.minDieselConsumption !== undefined
    ) {
      const minDiesel = parseFloat(searchCriteria.minDieselConsumption);
      filtered = filtered.filter((record) => record.dieselConsumption >= minDiesel);
    }

    if (
      searchCriteria.maxDieselConsumption !== null
      && searchCriteria.maxDieselConsumption !== undefined
    ) {
      const maxDiesel = parseFloat(searchCriteria.maxDieselConsumption);
      filtered = filtered.filter((record) => record.dieselConsumption <= maxDiesel);
    }

    // Running hours range filters
    if (searchCriteria.minRunningHours !== null && searchCriteria.minRunningHours !== undefined) {
      const minHours = parseFloat(searchCriteria.minRunningHours);
      filtered = filtered.filter((record) => record.runningHours >= minHours);
    }

    if (searchCriteria.maxRunningHours !== null && searchCriteria.maxRunningHours !== undefined) {
      const maxHours = parseFloat(searchCriteria.maxRunningHours);
      filtered = filtered.filter((record) => record.runningHours <= maxHours);
    }

    // Efficiency filters
    if (searchCriteria.minKwhPerLitre !== null && searchCriteria.minKwhPerLitre !== undefined) {
      const minEfficiency = parseFloat(searchCriteria.minKwhPerLitre);
      filtered = filtered.filter((record) => record.efficiencyMetrics.kwhPerLitre >= minEfficiency);
    }

    if (searchCriteria.maxKwhPerLitre !== null && searchCriteria.maxKwhPerLitre !== undefined) {
      const maxEfficiency = parseFloat(searchCriteria.maxKwhPerLitre);
      filtered = filtered.filter((record) => record.efficiencyMetrics.kwhPerLitre <= maxEfficiency);
    }

    // Efficiency status filter
    if (searchCriteria.efficiencyStatus) {
      filtered = filtered.filter((record) => {
        const { kwhPerLitre } = record.efficiencyMetrics;

        switch (searchCriteria.efficiencyStatus) {
          case 'high':
            return kwhPerLitre >= 3.0;
          case 'medium':
            return kwhPerLitre >= 2.0 && kwhPerLitre < 3.0;
          case 'low':
            return kwhPerLitre < 2.0;
          default:
            return true;
        }
      });
    }

    // Created by filter
    if (searchCriteria.createdBy) {
      const createdBy = searchCriteria.createdBy.toLowerCase();
      filtered = filtered.filter(
        (record) => record.createdBy && record.createdBy.toLowerCase().includes(createdBy),
      );
    }

    // Sort results
    if (searchCriteria.sortBy) {
      filtered = this.sortDGRecords(filtered, searchCriteria.sortBy, searchCriteria.sortDesc);
    }

    return filtered;
  }

  /**
   * Sorts DG records by specified field
   * @param {Array} records - DG records to sort
   * @param {string} sortBy - Field to sort by
   * @param {boolean} sortDesc - Sort in descending order
   * @returns {Array} Sorted DG records
   */
  static sortDGRecords(records, sortBy, sortDesc = false) {
    const sorted = [...records].sort((a, b) => {
      let aVal; let
        bVal;

      switch (sortBy) {
        case 'billingPeriod':
          aVal = new Date(a.billingPeriod.startDate);
          bVal = new Date(b.billingPeriod.startDate);
          break;
        case 'dgConsumption':
          aVal = a.dgConsumptionData.totalConsumption;
          bVal = b.dgConsumptionData.totalConsumption;
          break;
        case 'dieselConsumption':
          aVal = a.dieselConsumption;
          bVal = b.dieselConsumption;
          break;
        case 'runningHours':
          aVal = a.runningHours;
          bVal = b.runningHours;
          break;
        case 'kwhPerLitre':
          aVal = a.efficiencyMetrics.kwhPerLitre;
          bVal = b.efficiencyMetrics.kwhPerLitre;
          break;
        case 'litresPerHour':
          aVal = a.efficiencyMetrics.litresPerHour;
          bVal = b.efficiencyMetrics.litresPerHour;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
          break;
        default:
          aVal = a[sortBy];
          bVal = b[sortBy];
      }

      if (aVal < bVal) return sortDesc ? 1 : -1;
      if (aVal > bVal) return sortDesc ? -1 : 1;
      return 0;
    });

    return sorted;
  }

  /**
   * Gets search suggestions for DG billing records
   * @param {Array} records - Array of DG billing records
   * @param {string} field - Field to get suggestions for
   * @returns {Array} Array of suggestions
   */
  static getDGSearchSuggestions(records, field) {
    if (!records || records.length === 0) {
      return [];
    }

    const suggestions = new Set();

    records.forEach((record) => {
      switch (field) {
        case 'createdBy':
          if (record.createdBy) {
            suggestions.add(record.createdBy);
          }
          break;
        case 'dgConsumption':
          suggestions.add(record.dgConsumptionData.totalConsumption);
          break;
        case 'dieselConsumption':
          suggestions.add(record.dieselConsumption);
          break;
        case 'runningHours':
          suggestions.add(record.runningHours);
          break;
        case 'efficiency':
          suggestions.add(record.efficiencyMetrics.kwhPerLitre);
          suggestions.add(record.efficiencyMetrics.litresPerHour);
          break;
        default:
          break;
      }
    });

    return Array.from(suggestions).sort();
  }
}

export default {
  ExportService,
  SearchService,
};
